import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";

import { blankAddress } from "../components/quote-details/constants/quote-detail-constants.ts";

/**
 * Builds a `quoteState.{type}PostalCode` (`ZipCode`) from a saved address book
 * item's flat location fields.
 *
 * Applying a saved address updates `selectedAddressBookItems[type]` but must also
 * keep `quoteState.{type}PostalCode` in sync: several consumers read the location
 * from there (the shipping-label report body, the re-rate fingerprint, the
 * non-LTL city/state/zip row, etc.). Without this sync those consumers keep a
 * stale location left over from a prior template or postal-code selection.
 *
 * Only the fields consumers actually read are populated (postal code, city name,
 * state abbreviation, country code). The geocode ids and lat/long are left at
 * their blank defaults because nothing reads them off `quoteState.{type}PostalCode`.
 */
export const buildStopPostalCode = (
	address: Pick<
		AddressBookItemSummary,
		"postalCode" | "city" | "stateProvince" | "country"
	>,
): ZipCode => ({
	...blankAddress,
	postalCode: address.postalCode ?? "",
	city: {
		...blankAddress.city,
		name: address.city ?? "",
		state: {
			...blankAddress.city.state,
			abbreviation: address.stateProvince ?? "",
			countryCode: address.country ?? "",
		},
	},
});
